import { Divider } from '@dsg-tech/homefield-react';
import React, { useEffect, useState, useRef } from 'react';
import { HomefieldIconChevronRight } from '@dsg-tech/homefield-react-icons';
import { checkStores } from 'utility/utility';
import {
  ColumnCategory,
  PrimaryCategoryData,
  CsvCats,
  FeatureLinkItem,
  CombinedDataItem,
  CategoryItem,
  FeaturedElementData,
  HeaderFeaturedTaxonomy,
} from 'types';
import FeaturedElements from './FeaturedElements/FeaturedElements';

interface L1Props {
  L1LinksData: PrimaryCategoryData | null;
  viewPortBreakpoint: string;
  keyboardNavigation: boolean;
  handleClickL2LinksDisplay: (value: ColumnCategory, index: number) => void;
  handleKeyDownL2LinksDisplay: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    l1Data: ColumnCategory,
    index: number,
  ) => void;
  selectedL1Index: number;
  setSelectedL1Index: (value: number) => void;
  headerFeatured: HeaderFeaturedTaxonomy | null;
  selectedL0: PrimaryCategoryData | null;
  setShowL2Links: (value: boolean) => void;
}

const L1Links = ({
  L1LinksData,
  viewPortBreakpoint,
  keyboardNavigation,
  handleClickL2LinksDisplay,
  handleKeyDownL2LinksDisplay,
  selectedL1Index,
  setSelectedL1Index,
  headerFeatured,
  selectedL0,
  setShowL2Links,
}: L1Props) => {
  const [featuredElementsLinks, setFeaturedElementsLinks] = useState<
    null | undefined | FeaturedElementData
  >();
  const [delayHandler, setDelayHandler] = useState<ReturnType<typeof setTimeout>>();
  const [delayHandlerLinks, setDelayHandlerLinks] = useState<ReturnType<typeof setTimeout>>();
  const [combinedData, setCombinedData] = useState<CombinedDataItem[]>([]);
  const l1LinksBodyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (keyboardNavigation) {
      document.getElementById('shopAllL1')?.focus();
    }
  }, [L1LinksData]);

  useEffect(() => {
    if (viewPortBreakpoint === 'tablet' || viewPortBreakpoint === 'mobile') {
      const ele = document.getElementById('bodyContainer');
      if (ele && ele !== null) {
        ele.scrollTo({ top: 0 });
      }
    }
  }, []);

  useEffect(() => {
    if (l1LinksBodyContainerRef.current) {
      l1LinksBodyContainerRef.current.scrollTop = 0;
    }
  }, [L1LinksData]);

  const handleMouseEnter = (l1Link: CsvCats | ColumnCategory, index: number) => {
    if (viewPortBreakpoint === 'desktop') {
      setDelayHandler(
        setTimeout(() => {
          handleClickL2LinksDisplay(l1Link, index);
        }, 400),
      );
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(delayHandler);
  };

  const formatFeatureLink = (link: FeatureLinkItem): ColumnCategory => {
    const hasContent =
      Array.isArray(link['cta-beta']) &&
      link['cta-beta'].length > 0 &&
      Object.keys(link['cta-beta'][0]).length > 0;

    return {
      n: link.featuredFlyoutLabel,
      u: link.l0SeoUrl,
      // Only add ch object if there is content to display
      ...(hasContent
        ? {
            ch: link['cta-beta'].map((featureLink) => ({
              n: featureLink.linkTitle,
              u: featureLink.link,
            })),
          }
        : {}),
    };
  };

  const handleMouseEnterOnLinks = () => {
    if (viewPortBreakpoint === 'desktop') {
      setDelayHandlerLinks(
        setTimeout(() => {
          setSelectedL1Index(-1);
          setShowL2Links(false);
        }, 400),
      );
    }
  };

  const handleMouseLeaveOnLinks = () => {
    clearTimeout(delayHandlerLinks);
  };

  useEffect(() => {
    if (selectedL0 && headerFeatured) {
      const matchingFeaturedLinks = headerFeatured?.featuredLink?.find(
        (feat): feat is FeatureLinkItem =>
          // checks for the featuredFlyoutLabel that matches the featuredLink
          feat.l0SeoUrl === selectedL0.link,
      );

      // filters and updates state to include only the first match
      if (matchingFeaturedLinks) {
        setFeaturedElementsLinks({
          featuredLink: matchingFeaturedLinks,
          'featured-item': [],
        });
      } else {
        setFeaturedElementsLinks(null);
      }
    } else {
      setFeaturedElementsLinks(null);
    }
  }, [selectedL0, headerFeatured]);

  const mergeData = () => {
    const categories = L1LinksData?.csvData?.[0].col?.[0].cats?.map(
      (item): CategoryItem => ({ type: 'category', item }),
    );
    const features = featuredElementsLinks?.featuredLink
      ? [
          {
            type: 'feature',
            item: featuredElementsLinks?.featuredLink,
          },
        ]
      : [];
    return [...(categories || []), ...(features || [])];
  };

  useEffect(() => setCombinedData(mergeData()), [L1LinksData, featuredElementsLinks]);

  return (
    <div className="l1-links-container hmf-display-flex" id="l1LinksContainer">
      <div className="hmf-display-none hmf-flex-shrink-none hmf-display-xlg-block">
        <Divider vertical />
      </div>
      <div className="links-body hmf-display-flex hmf-flex-column hmf-justify-content-space-between">
        <div className="hmf-px-s hmf-pb-xs hmf-pt-xxs hmf-display-flex hmf-justify-content-between hmf-align-items-center">
          <span className="links-title label-base hmf-header-bold-m">{L1LinksData?.title}</span>
          {L1LinksData !== null && (
            <a
              className="hmf-body-bold-s hmf-px-s hmf-py-xxs hmf-ml-s shop-all hmf-text-transform-uppercase hmf-display-flex hmf-align-items-center hmf-justify-content-center"
              id="shopAllL1"
              tabIndex={0}
              href={checkStores(L1LinksData?.link)}
              data-em={`TopNav_${(L1LinksData?.title ?? '').replace(/ /g, '')}_All${(
                L1LinksData?.title ?? ''
              ).replace(/ /g, '')}`}>
              Shop All
            </a>
          )}
        </div>
        <div
          className="hmf-display-flex hmf-flex-column l1-links-body-container hmf-px-xlg-m hmf-pb-0 hmf-pb-xlg-xxxl"
          ref={l1LinksBodyContainerRef}>
          {L1LinksData && (
            <FeaturedElements
              featuredLink={featuredElementsLinks?.featuredLink}
              l0Title={L1LinksData.title}
              handleMouseEnterOnLinks={handleMouseEnterOnLinks}
              handleMouseLeaveOnLinks={handleMouseLeaveOnLinks}
            />
          )}
          {combinedData.map((data, index) => {
            const isSelected = selectedL1Index === index && viewPortBreakpoint === 'desktop';
            const content = data.type === 'category' ? data.item : formatFeatureLink(data.item);
            const label = data.type === 'category' ? data.item.n : data.item.featuredFlyoutLabel;
            const hasChildren = content.ch && content.ch.length > 0;
            const hasContent =
              Array.isArray(content.ch) &&
              content.ch.length > 0 &&
              Object.keys(content.ch[0]).length > 0;

            // if featured content is empty don't display it as not-category content
            if (data.type === 'feature' && !hasContent) {
              return null;
            }
            return hasChildren ? (
              <button
                key={`l1Link-${label}`}
                id={`l1Link-${index}`}
                className={`l0-link-content hmf-display-flex hmf-justify-content-between hmf-align-items-center hmf-px-s hmf-py-xxs hmf-text-align-left ${
                  isSelected && 'l0-link-content-selected'
                }`}
                type="button"
                onClick={() => handleClickL2LinksDisplay(content, index)}
                onKeyDown={(event) => {
                  handleKeyDownL2LinksDisplay(event, content, index);
                }}
                onMouseEnter={() => handleMouseEnter(content, index)}
                onMouseLeave={handleMouseLeave}
                tabIndex={0}
                aria-label={`Open ${label}`}>
                <div className="title-color hmf-body-l">{label}</div>
                {hasChildren && (
                  <div className="hmf-pl-xxs">
                    <HomefieldIconChevronRight width="24px" height="24px" />
                  </div>
                )}
              </button>
            ) : (
              <a
                key={`l1Link-${label}`}
                className="l0-link-content hmf-display-flex hmf-align-items-center hmf-px-s hmf-py-xxs title-color hmf-body-l"
                id={`l1Link-${index}`}
                tabIndex={0}
                aria-label={`Open ${label}`}
                onMouseEnter={handleMouseEnterOnLinks}
                onMouseLeave={handleMouseLeaveOnLinks}
                href={checkStores(content.u)}
                data-em={`TopNav_${(L1LinksData?.title ?? '').replace(/ /g, '')}_${(
                  label ?? ''
                ).replace(/ /g, '')}`}>
                {label}
              </a>
            );
          })}
        </div>
      </div>
      <div className="hmf-display-none hmf-flex-shrink-none hmf-display-xlg-block">
        <Divider vertical />
      </div>
    </div>
  );
};

export default L1Links;
